/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import endConsumerV2 from '../../utils/api/end-consumer-v2';

import { setNotificationMessage } from './NotificationManagementSlice';

interface EndConsumerFilter {
  startDate: string;
  endDate: string;
  range: string;
}

export interface UserDashboardResponse {
  _id: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  countryCode?: string;
  email: string;
  isActive: boolean;
  isPhoneNumberVerified: boolean;
  isEmailVerified: boolean;
  userCustomId: string;
  tenantId: string;
  googleSub?: string;
  preferredPaymentMethod?: string;

  wallet: {
    balance: string;
    deposit: string;
    reserved?: string;
    currency?: string | null;
  };

  ocpiToken?: {
    uid: string;
    type: string;
    contractId: string;
    valid: boolean;
    whitelist: string;
    lastUpdated: string;
    sync: {
      status: string;
      attempts: number;
      lastAttemptAt: string;
      lastError: string | null;
      ocpiReferenceId: string;
    };
  };

  profileImage: {
    _id?: string;
    url?: string;
    signedUrl?: string;
    fileName?: string;
    fileType?: string;
    fileSize?: string;
  }[];

  rfidCard: {
    _id?: string;
    rfidNo?: string;
    chargingPointSn?: string;
  }[];

  favouriteLocations: {
    _id?: string;
    stationName?: string;
  }[];

  personalChargingPoints: {
    _id?: string;
    chargingPointSn?: string;
  }[];

  vehicles: {
    _id: string;
    vinNumber?: string;
    carPlateNumber?: string;
    manufacturer?: string;
    model?: string;
    manufacturerYear?: string;
    connectorType?: string;
    fullChargeRange?: string;
    batteryCapacity?: string;
    isFavourite?: boolean;
    isSelected?: boolean;
    vehicleImageUrls: {
      _id?: string;
      url?: string;
      fileName?: string;
      fileType?: string;
      fileSize?: string;
    }[];
  }[];

  cards: {
    _id?: string;
    brand?: string;
    last4?: string;
    gateway?: string;
    isDefault?: boolean;
    isSelected?: boolean;
    isApplePayCard?: boolean;
    deletedAt?: string | null;
    cardHolderName?: string;
    expiryMonth?: string;
    expiryYear?: string;
    [key: string]: any;
  }[];

  deviceDetails: {
    _id?: string;
    fcmToken?: string;
    type?: string;
    accessToken?: string;
    expires?: string;
  }[];

  tokens: {
    _id?: string;
    access: {
      token: string;
      expires: string;
    };
    refresh: {
      token: string;
      expires: string;
    };
  }[];

  otp: any[];

  createdAt: string;
  updatedAt: string;
  __v: number;

  // Optional fields if another API returns them
  user?: {
    _id: string;
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    isActive: boolean;
    userCustomId: string;
    email?: string;
    profileImage: {
      url?: string;
      key?: string;
      signedUrl?: string;
    }[];
  };

  walletBalance?: string;
  hyperpayCustomerCard?: string;
  personalChargingPointCount?: number;
  favouriteStationCount?: number;
  totalPowerConsumedInKwh?: number;
  totalRevenue?: number;

  penaltySummary?: {
    penaltyCount: number;
    penaltyChargePaid: number;
  };
}

interface EndConsumerState {
  loading: boolean;
  endConsumerType: string;
  reportFilter: EndConsumerFilter;
  activePeriodIndex: number;
  selectedEndConsumer: {
    userCustomId: string;
    _id: string;
  } | null;
  basicInformation: UserDashboardResponse | null;
}

export const fetchBasicInformations = createAsyncThunk<
  UserDashboardResponse,
  { userCustomId: string }
>('basic-informations/fetchBasicInformations', async ({ userCustomId }, { dispatch }) => {
  if (!userCustomId) {
    throw new Error('userCustomId is required');
  }

  try {
    const response = await endConsumerV2.fetchBasicInformation(userCustomId);
    return response.data;
  } catch (error: any) {
    dispatch(
      setNotificationMessage({
        type: 'error',
        message: error?.response?.data?.message,
        code: error?.response?.data?.code,
        status: true,
      }),
    );
    throw error;
  }
});

const initialState: EndConsumerState = {
  loading: false,
  endConsumerType: 'commercial',
  reportFilter: {
    startDate: dayjs().subtract(24, 'hour').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    range: 'last24hours',
  },
  activePeriodIndex: 0,
  selectedEndConsumer: null,
  basicInformation: null,
};

const endConsumerSlice = createSlice({
  name: 'endConsumer',
  initialState,
  reducers: {
    /**
     * Function to update selectedChargingPoint
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSelectedEndConsumer: (state, action) => {
      state.selectedEndConsumer = action.payload;
    },

    /**
     * EndConsumer Data
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setEndConsumerData: (
      state,
      action: PayloadAction<{
        name: string;
        value: string;
      }>,
    ) => {
      const { name, value } = action.payload;

      if (name === 'endConsumerType') {
        return {
          ...state,
          endConsumerType: value,
        };
      }

      const keys = name.split('.');

      if (keys.length === 2 && keys[0] === 'reportFilter') {
        const filterKey = keys[1] as keyof EndConsumerFilter;
        return {
          ...state,
          activePeriodIndex: -1, // Reset selection when custom date is picked
          reportFilter: {
            ...state.reportFilter,
            [filterKey]: value,
          },
        };
      }

      return state;
    },

    /**
     * setTimePeriod
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setTimePeriod: (
      state,
      action: PayloadAction<{
        index: number;
        startDate: string;
        endDate: string;
        range: string;
      }>,
    ) => {
      const { index, startDate, endDate, range } = action.payload;
      return {
        ...state,
        activePeriodIndex: index,
        reportFilter: {
          startDate,
          endDate,
          range,
        },
      };
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder: ActionReducerMapBuilder<EndConsumerState>) => {
    builder
      .addCase(fetchBasicInformations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBasicInformations.fulfilled, (state, action) => {
        state.loading = false;
        state.basicInformation = action.payload;
      })
      .addCase(fetchBasicInformations.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { setEndConsumerData, setTimePeriod, setSelectedEndConsumer } =
  endConsumerSlice.actions;
export default endConsumerSlice.reducer;
